import { useEffect, useMemo, useState } from "react";

export type FunnelStage = "top" | "middle" | "bottom";

export interface UtmParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
}

// Bottom funnel: High intent - ready to take action
const BOTTOM_FUNNEL_PATTERNS = [
  // Specific campaign keywords
  "ppp removal", "ppp treatment", "ppp removal cream", "ppp removal surgery", "ppp removal at home",
  "penile papules removal", "penile papules removal cream",
  "pearly penile papules removal", "pearly papules removal",
  "get rid of ppp",
  // Action/solution keywords
  "remove", "treatment", "get rid", "eliminate", "cure", "fix", "method",
  "at home", "diy", "home treatment", "self treatment",
  // Purchase intent
  "cost", "price", "buy", "how much", "cheap", "affordable",
  // Specific solution seeking
  "how to remove", "how to treat", "how to get rid", "how to eliminate",
  "removal", "remover", "removing",
  // Urgency
  "fast", "quick", "easy", "simple", "overnight", "days",
];

// Middle funnel: Considering options, comparing
const MIDDLE_FUNNEL_PATTERNS = [
  // Specific campaign keywords
  "pearly penile papules", "tiny bumps on penis", "what is ppp", "ppp condition",
  "penile papules causes", "bumps on penis", "small bumps on penis",
  "penile skin bumps", "white bumps on glans", "non painful bumps on penis",
  // Research/comparison
  "before after", "before and after", "results", "does it work",
  "reviews", "testimonials", "comparison", "compare",
  // Evaluation
  "best", "top", "recommended", "effective", "safe", "safely",
  "worth it", "should i", "can i",
  // Options
  "options", "alternatives", "ways to", "methods",
  "doctor", "dermatologist", "urologist", "laser",
];

// Top funnel: Just learning, informational
const TOP_FUNNEL_PATTERNS = [
  // Specific campaign keywords
  "pearly penile", "pearly papules", "remove ppp", "papules on skin",
  "pearly papules treatment", "ppp cure", "small bumps under skin",
  "ppp penile", "ppp men", "hirsuties papillaris genitalis",
  // Informational queries
  "what is", "what are", "what causes", "why do i have",
  "difference", "vs", "versus", "or",
  // Understanding
  "cause", "causes", "normal", "common", "symptoms",
  "identify", "identification", "recognize",
  // Visual research
  "pictures", "photos", "images", "look like", "looks like",
  // General info
  "meaning", "definition", "explain", "about",
  "is it", "are they", "do i have",
];

// Combine all patterns with their funnel stage, sorted by specificity (longer = more specific)
const ALL_PATTERNS: Array<{ pattern: string; stage: FunnelStage }> = [
  ...BOTTOM_FUNNEL_PATTERNS.map(p => ({ pattern: p, stage: "bottom" as FunnelStage })),
  ...MIDDLE_FUNNEL_PATTERNS.map(p => ({ pattern: p, stage: "middle" as FunnelStage })),
  ...TOP_FUNNEL_PATTERNS.map(p => ({ pattern: p, stage: "top" as FunnelStage })),
].sort((a, b) => b.pattern.length - a.pattern.length); // Longer patterns first

const detectFunnelStage = (searchText: string): FunnelStage => {
  const text = searchText.toLowerCase();
  
  // Check patterns in order of specificity (longer/more specific first)
  for (const { pattern, stage } of ALL_PATTERNS) {
    if (text.includes(pattern)) {
      return stage;
    }
  }
  
  // Default: if has UTM but no pattern match, assume middle
  return "middle";
};

const EMPTY_UTM: UtmParams = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
};

export const useUtmDetection = () => {
  const [utmParams, setUtmParams] = useState<UtmParams>(EMPTY_UTM);

  // Read UTMs after hydration — window is not available during SSR
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setUtmParams({
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
      utm_term: params.get("utm_term"),
    });
  }, []);

  // Has any UTM parameter = came from a campaign
  const isFromCampaign = useMemo(() => {
    return !!(
      utmParams.utm_source ||
      utmParams.utm_medium ||
      utmParams.utm_campaign ||
      utmParams.utm_content ||
      utmParams.utm_term
    );
  }, [utmParams]);

  // Detect funnel stage based on search intent
  const funnelStage = useMemo((): FunnelStage => {
    // Direct visitor = top of funnel (just discovered organically)
    if (!isFromCampaign) {
      return "top";
    }

    // Combine all UTM text to analyze intent
    // Priority: utm_term (actual keyword) > utm_content > utm_campaign
    const searchText = [
      utmParams.utm_term,
      utmParams.utm_content,
      utmParams.utm_campaign,
    ]
      .filter(Boolean)
      .join(" ");

    if (!searchText) {
      return "middle"; // Has UTM but no analyzable text
    }

    return detectFunnelStage(searchText);
  }, [utmParams, isFromCampaign]);

  // Direct visitor = no UTM (top of funnel)
  const isDirectVisitor = !isFromCampaign;

  return {
    utmParams,
    isFromCampaign,
    isDirectVisitor,
    funnelStage,
  };
};

